import { useCallback, useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Button, Card, Drawer, Space, Typography, message } from 'antd';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CSSProperties } from 'react';

import {
  createCase,
  deleteCase,
  freezeCase,
  listCases,
  recordReview,
  startReview,
  submitCase,
  withdrawCase,
} from '@/api/cases';
import type {
  CreateCaseBody,
  DeclarationCase,
  ListCasesParams,
  RecordReviewBody,
  WithdrawBody,
} from '@/api/cases';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSkeleton } from '@/components/states/LoadingSkeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { EmptyState } from '@/components/states/EmptyState';

import { CaseFilters } from './components/CaseFilters';
import type { CaseFiltersValue } from './components/CaseFilters';
import { KanbanBoard } from './components/KanbanBoard';
import { RecordReviewModal } from './components/RecordReviewModal';
import { WithdrawModal } from './components/WithdrawModal';
import { CreateCaseModal } from './components/CreateCaseModal';
import { GenerateArtifactsModal } from './components/GenerateArtifactsModal';
import { ArtifactsListPanel } from './components/ArtifactsListPanel';
import { AssignmentModal } from './components/AssignmentModal';

/**
 * 申报案 Kanban 主页（v4 M3-ρ-3）— 第三个真接口业务页。
 *
 * 数据流：
 *   GET /declaration-cases?subject_id?&policy_pack_id?&status?&declare_year?&page&page_size
 *   POST /…/{id}/freeze | submit | start-review | record-review | withdraw
 *   DELETE /…/{id}                        — 仅 draft
 *
 * 每个状态机操作 onSuccess → invalidateQueries(['cases', filters])
 * → 列表 refetch → 卡片自动从源列移动到目标列。
 *
 * RBAC：
 *   - CaseActionsMenu 按 useAuth().role 过滤按钮（STAFF 不见 start-review / record-review）
 *   - ENTERPRISE 整页 RoleGuard 拦截（在 routes.tsx 用 menuKey="/cases"）
 *
 * 不变量：
 *   - queryKey 含完整 filters → 防跨参数串号（ρ-1 教训）
 *   - encodeURIComponent path params（已在 cases.ts 封装）
 *   - 0 hex 硬编码（ρ-1/ρ-2 教训）
 */
export function CasesPage() {
  const [params, setParams] = useSearchParams();
  const { role } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // URL → filters（subject_id / policy_pack_id / declare_year / status 可选）
  const filters: CaseFiltersValue = useMemo(
    () => ({
      subject_id: params.get('subject_id') ?? '',
      policy_pack_id: params.get('policy_pack_id') ?? '',
      declare_year: params.get('declare_year')
        ? Number(params.get('declare_year'))
        : null,
    }),
    [params],
  );

  const updateFilters = (next: CaseFiltersValue) => {
    const p = new URLSearchParams(params);
    if (next.subject_id) p.set('subject_id', next.subject_id);
    else p.delete('subject_id');
    if (next.policy_pack_id) p.set('policy_pack_id', next.policy_pack_id);
    else p.delete('policy_pack_id');
    if (next.declare_year != null)
      p.set('declare_year', String(next.declare_year));
    else p.delete('declare_year');
    setParams(p, { replace: true });
  };

  // queryKey 含完整 filters（ρ-1 教训：防跨参数串号）
  const apiParams: ListCasesParams = useMemo(
    () => ({
      subject_id: filters.subject_id || undefined,
      policy_pack_id: filters.policy_pack_id || undefined,
      declare_year: filters.declare_year ?? undefined,
      page: 1,
      page_size: 200,
    }),
    [filters],
  );

  const query = useQuery({
    queryKey: ['cases', apiParams],
    queryFn: () => listCases(apiParams),
    staleTime: 30_000,
  });

  // === 模态框状态 ===
  const [createOpen, setCreateOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<DeclarationCase | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<DeclarationCase | null>(
    null,
  );
  // M3-ρ-4：生成产物 modal + 产物列表抽屉
  const [generateTarget, setGenerateTarget] = useState<DeclarationCase | null>(
    null,
  );
  const [artifactsTarget, setArtifactsTarget] = useState<DeclarationCase | null>(
    null,
  );
  // S20-C0:指派 / 改派 Modal
  const [assignTarget, setAssignTarget] = useState<DeclarationCase | null>(null);

  // === 当前正在 mutation 的 case_id（按钮 loading） ===
  const [pendingId, setPendingId] = useState<string | null>(null);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['cases'] });
  }, [qc]);

  // === Mutations ===
  // 统一 onError：message.error；onSettled：清 pendingId
  const freezeMut = useMutation({
    mutationFn: (c: DeclarationCase) => freezeCase(c.id),
    onMutate: (c) => setPendingId(c.id),
    onSuccess: () => {
      message.success('已冻结');
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '冻结失败'),
    onSettled: () => setPendingId(null),
  });

  const submitMut = useMutation({
    mutationFn: (c: DeclarationCase) => submitCase(c.id),
    onMutate: (c) => setPendingId(c.id),
    onSuccess: () => {
      message.success('已提交');
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '提交失败'),
    onSettled: () => setPendingId(null),
  });

  const startReviewMut = useMutation({
    mutationFn: (c: DeclarationCase) => startReview(c.id),
    onMutate: (c) => setPendingId(c.id),
    onSuccess: () => {
      message.success('已开始审查');
      invalidate();
    },
    onError: (err) =>
      message.error((err as Error)?.message || '开始审查失败'),
    onSettled: () => setPendingId(null),
  });

  const recordReviewMut = useMutation({
    mutationFn: (vars: { id: string; body: RecordReviewBody }) =>
      recordReview(vars.id, vars.body),
    onMutate: (vars) => setPendingId(vars.id),
    onSuccess: () => {
      message.success('审查结果已记录');
      setReviewTarget(null);
      invalidate();
    },
    onError: (err) =>
      message.error((err as Error)?.message || '记录失败'),
    onSettled: () => setPendingId(null),
  });

  const withdrawMut = useMutation({
    mutationFn: (vars: { id: string; body: WithdrawBody }) =>
      withdrawCase(vars.id, vars.body),
    onMutate: (vars) => setPendingId(vars.id),
    onSuccess: () => {
      message.success('已撤回');
      setWithdrawTarget(null);
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '撤回失败'),
    onSettled: () => setPendingId(null),
  });

  const deleteMut = useMutation({
    mutationFn: (c: DeclarationCase) => deleteCase(c.id),
    onMutate: (c) => setPendingId(c.id),
    onSuccess: () => {
      message.success('已删除');
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '删除失败'),
    onSettled: () => setPendingId(null),
  });

  const createMut = useMutation({
    mutationFn: (body: CreateCaseBody) => createCase(body),
    onSuccess: () => {
      message.success('已创建草稿申报案');
      setCreateOpen(false);
      invalidate();
    },
    onError: (err) => message.error((err as Error)?.message || '创建失败'),
  });

  const subtitle = `Kanban · GET /declaration-cases · 状态机 7 态 · 真后端`;

  // M3-ρ-3 Reviewer high#1/2: 用后端 total 而非前端切片长度，避免 page_size=200 误导
  const totalCount = query.data?.total ?? 0;
  const loadedCount = query.data?.items.length ?? 0;
  const truncated = loadedCount > 0 && loadedCount < totalCount;

  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      <Card styles={{ body: { padding: 14 } }}>
        <Space
          direction="vertical"
          size={10}
          style={{ width: '100%' }}
        >
          <Space
            style={{ width: '100%', justifyContent: 'space-between' }}
            align="center"
            wrap
          >
            <div>
              <Typography.Title
                level={4}
                style={{ margin: 0, color: 'var(--color-text-primary)' }}
              >
                申报案跟踪
              </Typography.Title>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 'var(--font-size-sm)' }}
              >
                {subtitle}
              </Typography.Text>
            </div>
            <Space>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 'var(--font-size-sm)' }}
              >
                共 <strong style={countStyle}>{totalCount}</strong> 个
                {truncated && (
                  <>
                    （仅展示前 <strong style={countStyle}>{loadedCount}</strong> 条）
                  </>
                )}
              </Typography.Text>
              <Button
                type="primary"
                icon={<IconPlus size={14} />}
                onClick={() => setCreateOpen(true)}
              >
                新建申报案
              </Button>
            </Space>
          </Space>
          <CaseFilters
            value={filters}
            onChange={updateFilters}
            onRefresh={() => query.refetch()}
            refreshing={query.isFetching}
          />
        </Space>
      </Card>

      {query.isLoading && <LoadingSkeleton rows={6} />}

      {query.isError && (
        <ErrorState
          title="加载申报案失败"
          description={(query.error as Error)?.message}
          onRetry={() => query.refetch()}
        />
      )}

      {query.data && totalCount === 0 && (
        <EmptyState
          title="暂无申报案"
          description="可点右上「新建申报案」开始；或调整上方筛选条件。"
        />
      )}

      {query.data && totalCount > 0 && (
        <KanbanBoard
          cases={query.data.items}
          role={role}
          pendingId={pendingId}
          onFreeze={(c) => freezeMut.mutate(c)}
          onSubmit={(c) => submitMut.mutate(c)}
          onStartReview={(c) => startReviewMut.mutate(c)}
          onRecordReview={(c) => setReviewTarget(c)}
          onWithdraw={(c) => setWithdrawTarget(c)}
          onDelete={(c) => deleteMut.mutate(c)}
          onGenerateArtifacts={(c) => setGenerateTarget(c)}
          onAssign={(c) => setAssignTarget(c)}
          onView={(c) => navigate(`/cases/${c.id}`)}
        />
      )}

      <CreateCaseModal
        open={createOpen}
        loading={createMut.isPending}
        onCancel={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate(body)}
        defaults={{
          subject_id: filters.subject_id || undefined,
          policy_pack_id: filters.policy_pack_id || undefined,
          declare_year: filters.declare_year,
        }}
      />

      <RecordReviewModal
        open={reviewTarget !== null}
        loading={recordReviewMut.isPending}
        onCancel={() => setReviewTarget(null)}
        onSubmit={(body) => {
          if (reviewTarget) {
            recordReviewMut.mutate({ id: reviewTarget.id, body });
          }
        }}
      />

      <WithdrawModal
        open={withdrawTarget !== null}
        loading={withdrawMut.isPending}
        onCancel={() => setWithdrawTarget(null)}
        onSubmit={(body) => {
          if (withdrawTarget) {
            withdrawMut.mutate({ id: withdrawTarget.id, body });
          }
        }}
      />

      <GenerateArtifactsModal
        open={generateTarget !== null}
        case_={generateTarget}
        onCancel={() => {
          // 生成成功后无缝把抽屉切到同一 case，方便用户立刻看列表
          const finished = generateTarget;
          setGenerateTarget(null);
          if (finished) setArtifactsTarget(finished);
        }}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['artifacts'] });
        }}
      />

      <AssignmentModal
        open={assignTarget !== null}
        case_={assignTarget}
        onClose={() => setAssignTarget(null)}
      />

      <Drawer
        title={
          artifactsTarget
            ? `产物列表 · ${artifactsTarget.subject_id.slice(0, 8)}…`
            : '产物列表'
        }
        placement="right"
        width={760}
        open={artifactsTarget !== null}
        onClose={() => setArtifactsTarget(null)}
        destroyOnClose
      >
        <ArtifactsListPanel
          caseId={artifactsTarget?.id ?? null}
          enabled={artifactsTarget !== null}
        />
      </Drawer>
    </Space>
  );
}

const countStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-mono)',
};
